import { ReactElement, ReactNode } from 'react';

export enum SendProgress {
    NotStarted,
    InProgress,
    Success,
    Failed,
}

export interface PromiseCheckListItemProps {
    progress: SendProgress;
    text: ReactNode;
}

export interface PromiseCheckListProps {
    className?: string;
    children: ReactElement<PromiseCheckListItemProps>[];
}

function sleep(duration: number) {
    return new Promise((resolve, reject) => {
        window.setTimeout(() => {
            resolve(null);
        }, duration);
    });
}

export async function withProgressUpdates<T>(
    promise: Promise<T>,
    updateProgress: (progress: SendProgress) => void,
    sleepDuration: number = 0
): Promise<T> {
    try {
        updateProgress(SendProgress.InProgress);
        const sleepPromise = sleepDuration ? sleep(sleepDuration) : null;

        const result = await promise;

        if (sleepPromise) {
            await sleepPromise;
        }

        updateProgress(SendProgress.Success);

        return result;
    } catch (e: any) {
        updateProgress(SendProgress.Failed);
        throw e;
    }
}

export function allComplete<T extends { [key: string]: SendProgress }>(data: T) {
    return Object.values(data).every((x) => x === SendProgress.Success || x === SendProgress.Failed);
}

export { default as PromiseCheckList } from './PromiseCheckList';
export { default as PromiseCheckListItem } from './PromiseCheckListItem';